import React, { useRef, useEffect } from 'react';
import { X, GitCompare } from 'lucide-react';

const VideoComparisonSetup = ({ slotA, slotB, onClearSlot, onCompareStart }) => {
  const startBtnRef = useRef(null);

  // Auto-scroll to start button when both slots are filled
  useEffect(() => {
    if (slotA && slotB && startBtnRef.current) {
      startBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [slotA, slotB]);

  const renderSlot = (slot, label, slotKey) => (
    <div className={`cmp-slot ${slot ? 'filled' : 'empty'}`}>
      <div className="slot-label">{label}</div>
      {slot ? (
        <div className="slot-content">
          {slot.thumbnailUrl ? (
            <img src={slot.thumbnailUrl} alt={slot.label} className="slot-thumbnail" />
          ) : (
            <div className="slot-thumbnail-placeholder">No thumbnail</div>
          )}
          <div className="slot-info">
            <div className="slot-element">{slot.elementType.replace('_', ' ').toUpperCase()}</div>
            <div className="slot-date">{slot.date}</div>
          </div>
          <button
            className="slot-clear-btn"
            onClick={() => onClearSlot(slotKey)}
            title="Remove"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="slot-empty-msg">Select a video from history below</div>
      )}
    </div>
  );

  return (
    <div className="comparison-setup">
      <div className="cmp-grid">
        {renderSlot(slotA, 'A - Reference', 'A')}
        <div className="cmp-vs">VS</div>
        {renderSlot(slotB, 'B - Comparison', 'B')}
      </div>

      <button
        ref={startBtnRef}
        className="compare-start-btn"
        disabled={!slotA || !slotB}
        onClick={onCompareStart}
      >
        <GitCompare size={18} />
        Start Frame-by-Frame Comparison
      </button>

      <style>{`
        .comparison-setup {
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .cmp-grid {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .cmp-vs {
          font-weight: 700;
          font-size: 18px;
          color: #6b7280;
          flex-shrink: 0;
        }
        .cmp-slot {
          flex: 1;
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          padding: 12px;
          min-height: 80px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .cmp-slot.filled {
          border-style: solid;
          border-color: #3b82f6;
          background: #eff6ff;
        }
        .slot-label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6b7280;
        }
        .slot-content {
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
        }
        .slot-thumbnail {
          width: 60px;
          height: 40px;
          object-fit: cover;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
        }
        .slot-thumbnail-placeholder {
          width: 60px;
          height: 40px;
          background: #1a1a2e;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          color: #9ca3af;
        }
        .slot-info {
          flex: 1;
        }
        .slot-element {
          font-weight: 600;
          font-size: 13px;
          color: #1f2937;
        }
        .slot-date {
          font-size: 11px;
          color: #6b7280;
        }
        .slot-clear-btn {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
        }
        .slot-empty-msg {
          color: #9ca3af;
          font-size: 13px;
          text-align: center;
          padding: 8px 0;
        }
        .compare-start-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          margin-top: 16px;
          padding: 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .compare-start-btn:hover:not(:disabled) {
          background: #2563eb;
        }
        .compare-start-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Dark theme */
        body[data-theme="dark"] .comparison-setup {
          background: #1e293b;
        }
        body[data-theme="dark"] .cmp-slot {
          border-color: #475569;
        }
        body[data-theme="dark"] .cmp-slot.filled {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }
        body[data-theme="dark"] .slot-label {
          color: #94a3b8;
        }
        body[data-theme="dark"] .slot-element {
          color: #f1f5f9;
        }
        body[data-theme="dark"] .slot-date {
          color: #64748b;
        }
        body[data-theme="dark"] .slot-empty-msg {
          color: #64748b;
        }
        body[data-theme="dark"] .cmp-vs {
          color: #94a3b8;
        }

        /* Gold theme */
        body[data-theme="gold"] .comparison-setup {
          background: rgba(42, 42, 42, 0.9);
        }
        body[data-theme="gold"] .cmp-slot {
          border-color: rgba(212, 175, 55, 0.3);
        }
        body[data-theme="gold"] .cmp-slot.filled {
          border-color: #D4AF37;
          background: rgba(212, 175, 55, 0.1);
        }
        body[data-theme="gold"] .slot-label {
          color: rgba(244, 228, 188, 0.6);
        }
        body[data-theme="gold"] .slot-element {
          color: #f4e4bc;
        }
        body[data-theme="gold"] .slot-date {
          color: rgba(244, 228, 188, 0.5);
        }
        body[data-theme="gold"] .slot-empty-msg {
          color: rgba(244, 228, 188, 0.4);
        }
        body[data-theme="gold"] .cmp-vs {
          color: #D4AF37;
        }
        body[data-theme="gold"] .compare-start-btn {
          background: linear-gradient(135deg, #8B6914 0%, #D4AF37 100%);
        }
        body[data-theme="gold"] .compare-start-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #a07d1a 0%, #e0bf4a 100%);
        }

        @media (max-width: 600px) {
          .cmp-grid {
            flex-direction: column;
          }
          .cmp-vs {
            font-size: 14px;
          }
          .cmp-slot {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoComparisonSetup;
